import { useClient } from '@cortex/react-query';
import type { GroundTruth, WorkflowListItem } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type CSSProperties } from 'react';

import { BackButton } from '../../components/common/BackButton';
import { InfoHint } from '../../components/common/InfoHint';
import { PageHeader } from '../../components/common/PageHeader';
import { routes } from '../../lib/constants';

export const Route = createFileRoute('/ground-truth/bulk')({ component: BulkGroundTruthPage });

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

interface IEntry {
  nodeId?: string | null;
  input: Record<string, unknown>;
  expectedOutput?: Record<string, unknown> | null;
  name?: string;
  notes?: string;
}

/**
 * Parse the textarea as either a top-level JSON array of entries, OR
 * newline-delimited JSON (one entry object per line). Both are common when
 * exporting from spreadsheets or ad-hoc scripts.
 */
function parseEntries(raw: string): { ok: true; value: IEntry[] } | { ok: false; error: string } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: false, error: 'No entries provided' };
  // Top-level array
  if (trimmed.startsWith('[')) {
    try {
      const arr = JSON.parse(trimmed) as unknown;
      if (!Array.isArray(arr)) return { ok: false, error: 'Top-level value must be an array' };
      return { ok: true, value: arr as IEntry[] };
    } catch (err) {
      return { ok: false, error: (err as Error).message };
    }
  }
  // NDJSON fallback — one object per non-blank line
  const lines = trimmed.split('\n').filter((l) => l.trim().length > 0);
  try {
    const parsed = lines.map((line, i) => {
      const obj = JSON.parse(line) as unknown;
      if (!obj || typeof obj !== 'object' || Array.isArray(obj))
        throw new Error(`Line ${i + 1}: not a JSON object`);
      return obj as IEntry;
    });
    return { ok: true, value: parsed };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

function BulkGroundTruthPage() {
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();

  const [workflowId, setWorkflowId] = useState('');
  const [body, setBody] = useState('');

  const workflows = useQuery({
    queryKey: ['workflows', 'all-for-gt'],
    queryFn: () =>
      client.http.get('v1/workflows?limit=200').json<IListEnvelope<WorkflowListItem>>(),
  });

  const parsed = parseEntries(body);
  const validation =
    body.trim().length === 0
      ? { kind: 'empty' as const }
      : !parsed.ok
        ? { kind: 'error' as const, error: parsed.error }
        : parsed.value.length === 0
          ? { kind: 'error' as const, error: 'No entries found' }
          : parsed.value.length > 1000
            ? {
                kind: 'error' as const,
                error: `Bulk endpoint accepts at most 1000 entries; got ${parsed.value.length}.`,
              }
            : {
                kind: 'ok' as const,
                entries: parsed.value,
                withExpected: parsed.value.filter((e) => e.expectedOutput).length,
                nodeScoped: parsed.value.filter((e) => e.nodeId).length,
              };

  const create = useMutation({
    mutationFn: () => {
      if (validation.kind !== 'ok') return Promise.reject(new Error('Fix validation errors'));
      return client.http
        .post('v1/ground-truth/bulk', {
          json: { workflowId, entries: validation.entries },
        })
        .json<{ created: number; entries: GroundTruth[] }>();
    },
    onSuccess: async () => {
      try {
        await qc.invalidateQueries({ queryKey: ['ground-truth'] });
      } catch (err) {
        console.error(err);
      }
      try {
        await nav({ to: routes.GROUND_TRUTH });
      } catch (err) {
        console.error(err);
      }
    },
  });

  const canSubmit = workflowId.length > 0 && validation.kind === 'ok' && !create.isPending;

  const example =
    '[\n  {\n    "input": { "patientId": "p-1" },\n    "expectedOutput": { "disposition": "PROCEED" },\n    "name": "happy-path"\n  },\n  {\n    "nodeId": "classify",\n    "input": { "patientId": "p-2" },\n    "expectedOutput": { "tags": ["stable"] }\n  }\n]';

  return (
    <>
      <PageHeader
        title="Bulk import ground truth"
        actions={<BackButton to={routes.GROUND_TRUTH} label="Ground truth" />}
      />
      <div className="card" style={{ maxWidth: 920 }}>
        <div className="card-body">
          <form
            style={stack3}
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) create.mutate();
            }}
          >
            <label className="field">
              <span className="field-label">
                Workflow
                <InfoHint
                  text="All entries in this import are attached to this workflow. To import for multiple workflows, run bulk import once per workflow."
                  side="right"
                />
              </span>
              <select
                required={true}
                className="select-native"
                value={workflowId}
                onChange={(e) => setWorkflowId(e.target.value)}
              >
                <option value="">— Select a workflow —</option>
                {(workflows.data?.items ?? []).map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name} ({w.id.slice(0, 8)})
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field-label">
                Entries
                <InfoHint
                  text={
                    <>
                      Each entry is an object with at minimum <code>input</code>. Optional fields:{' '}
                      <code>nodeId</code> (set for node-scoped), <code>expectedOutput</code>,{' '}
                      <code>name</code>, <code>notes</code>.
                      <br />
                      <br />
                      Accepts a top-level JSON array <i>or</i> newline-delimited JSON (one object
                      per line) — whichever your tooling produces.
                    </>
                  }
                  side="right"
                />
              </span>
              <textarea
                required={true}
                className="textarea t-mono"
                rows={16}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                style={{ fontSize: 12 }}
                placeholder={example}
              />
              {validation.kind === 'error' ? (
                <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                  {validation.error}
                </span>
              ) : validation.kind === 'ok' ? (
                <span className="t-xs t-muted">
                  {validation.entries.length} entries · {validation.withExpected} with expected
                  output · {validation.nodeScoped} node-scoped ·{' '}
                  {validation.entries.length - validation.nodeScoped} workflow-scoped
                </span>
              ) : (
                <span className="t-xs t-muted">Paste JSON to validate.</span>
              )}
            </label>

            <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
              <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
                {create.isPending
                  ? 'Importing…'
                  : validation.kind === 'ok'
                    ? `Import ${validation.entries.length} entries`
                    : 'Import'}
              </button>
              {create.error ? (
                <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                  {(create.error as Error).message}
                </span>
              ) : null}
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
